// mintWorkflowStepTaskNodes.js

import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { upsertEdge, upsertEdges } from 'services/nodes/NodeEdgeService';
import { createNode as createNodeDoc, createNodes } from 'services/nodes/NodeWriteService';
import { createChecklist } from 'services/checklists/ChecklistService';
import {
  MISSION_TASK_HANDLED_BY,
  normalizeMissionPeople,
} from 'missions/taskmodal/missionTaskModalUtils';
import { resolveTaskPeopleFieldName } from 'node/missions/mission/utils/workflowStepNodeUtils';
import {
  buildWorkflowTaskChecklistSeedItems,
  readWorkflowTaskChecklistRecipe,
  resolveTaskChecklistFieldName,
} from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { collectChecklistResourceNodeKeys } from 'utilities/ChecklistItemResources';
import { getNodesByKeys } from 'services/nodes/NodeReadService';
import {
  collectWorkflowStepChecklistResourceLinks,
} from 'node/missions/mission/utils/workflowStepChecklistResourceUtils';

export const WORKFLOW_STEP_TASK_TYPE_KEY = 'TASK';

export {
  applyWorkflowStepNodeToRunTask,
  collectWorkflowStepNodeKeys,
  readWorkflowStepNodeAssignees,
  resolveTaskPeopleFieldName,
} from 'node/missions/mission/utils/workflowStepNodeUtils';

function buildTaskNodeDataBag({
  assignees = [],
  peopleFieldName = 'assignees',
  typeDef = null,
} = {}) {
  const normalizedPeople = normalizeMissionPeople(assignees);
  const fieldName = resolveTaskPeopleFieldName(typeDef)
    || String(peopleFieldName || '').trim()
    || 'assignees';

  if (!normalizedPeople.length) return {};

  return {
    [fieldName]: normalizedPeople,
  };
}

/**
 * Builds an in-memory TASK node for a people workflow step (no writes).
 *
 * @param {object} options
 * @returns {Promise<object>} TASK node payload ready to persist.
 */
export async function buildWorkflowStepTaskNode({
  anchorNode = null,
  assignees = [],
  baseKey = null,
  description = '',
  title = '',
  user = null,
} = {}) {
  const anchorKey = String(anchorNode?.key || '').trim();
  const resolvedBaseKey = String(baseKey || anchorNode?.baseKey || '').trim() || null;
  if (!anchorKey || !user?.key) {
    throw new Error('Cannot mint workflow step task without anchor and user.');
  }

  const typeDef = getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null;
  const taskNode = await createNodeFromType(WORKFLOW_STEP_TASK_TYPE_KEY, user, resolvedBaseKey);

  taskNode.title = String(title || '').trim() || 'Untitled task';
  // TASK schema maps Description → root subtitle (field type: subtitle).
  taskNode.subtitle = String(description || '').trim();
  taskNode.parentTitle = String(anchorNode?.title || '').trim();
  taskNode.parentType = anchorNode?.type || null;
  taskNode.profileRoot = false;
  taskNode.contributorKey = user.key;

  const dataKey = String(typeDef?.dataSection || '').trim()
    || `data_${String(WORKFLOW_STEP_TASK_TYPE_KEY).toLowerCase()}`;
  const existingData = taskNode[dataKey] && typeof taskNode[dataKey] === 'object'
    ? { ...taskNode[dataKey] }
    : {};
  // Drop legacy singular key so we never persist the wrong field name.
  delete existingData.assignee;

  const peopleFieldName = resolveTaskPeopleFieldName(typeDef);
  const peoplePayload = buildTaskNodeDataBag({
    assignees,
    peopleFieldName,
    typeDef,
  });

  taskNode[dataKey] = {
    ...existingData,
    ...peoplePayload,
  };

  // Guaranteed write even when create defaults left an empty people field.
  if (peoplePayload[peopleFieldName]) {
    taskNode[dataKey][peopleFieldName] = peoplePayload[peopleFieldName];
  }

  // Mark as workflow-minted so walk-artifact Activity lifts to the host
  // instead of living only on the step TASK.
  taskNode.workflowStepMint = true;

  return taskNode;
}

/**
 * mintWorkflowStepTaskNode
 *
 * Creates one TASK for a people workflow step and links it to the anchor.
 *
 * @param {object} options
 * @returns {Promise<object>} Created TASK node.
 */
export async function mintWorkflowStepTaskNode({
  anchorNode = null,
  assignees = [],
  baseKey = null,
  description = '',
  title = '',
  user = null,
} = {}) {
  const taskNode = await buildWorkflowStepTaskNode({
    anchorNode,
    assignees,
    baseKey,
    description,
    title,
    user,
  });

  await createNodeDoc(taskNode.key, taskNode);
  await upsertEdge(
    String(anchorNode?.key || '').trim(),
    taskNode.key,
    {
      ...buildHierarchyEdgeOptions({
        parentNode: anchorNode,
        childNode: taskNode,
        baseKey: String(baseKey || anchorNode?.baseKey || taskNode?.baseKey || '').trim() || null,
      }),
      // Workflow launch mints many steps at once — don't spam host Activity.
      skipActivity: true,
    }
  );

  return taskNode;
}

/**
 * Seeds a Task Checklist recipe onto a minted TASK checklist field instance.
 * Brand-new mint path skips the empty "list existing" round trip.
 *
 * @param {object} options
 * @returns {Promise<object|null>}
 */
export async function seedWorkflowStepTaskChecklist({
  recipeItems = [],
  stepNode = null,
  typeDef = null,
  user = null,
} = {}) {
  const seedItems = buildWorkflowTaskChecklistSeedItems(recipeItems);
  const nodeKey = String(stepNode?.key || '').trim();
  const baseKey = String(stepNode?.baseKey || '').trim();
  const userKey = String(user?.key || user?.uid || '').trim();

  if (!seedItems.length || !nodeKey || !baseKey || !userKey) {
    return null;
  }

  const fieldName = resolveTaskChecklistFieldName(typeDef);

  return createChecklist({
    actor: user,
    baseKey,
    fieldLabel: 'Checklist',
    title: 'Checklist',
    items: seedItems,
    anchor: {
      kind: 'node',
      nodeKey,
      nodeType: WORKFLOW_STEP_TASK_TYPE_KEY,
      fieldName,
      label: String(stepNode?.title || '').trim() || fieldName || nodeKey,
    },
    userKey,
  });
}

/**
 * reportMintProgress
 *
 * @param {Function|null|undefined} onProgress
 * @param {object} payload
 */
function reportMintProgress(onProgress, payload) {
  if (typeof onProgress !== 'function') return;
  try {
    onProgress(payload);
  } catch (error) {
    console.warn('[mintWorkflowStepTaskNodes] onProgress failed:', error);
  }
}

/**
 * mintWorkflowStepTaskNodesForRun
 *
 * Mints a TASK for each people step missing `nodeKey`, then returns updated tasks.
 * Uses batched node writes + batched edge links so Start scales with playbook size.
 *
 * @param {object} options
 * @param {Function} [options.onProgress] - Optional progress reporter.
 * @returns {Promise<object[]>}
 */
export async function mintWorkflowStepTaskNodesForRun({
  anchorNode = null,
  baseKey = null,
  onProgress = null,
  tasks = [],
  user = null,
} = {}) {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const resolvedBaseKey = String(baseKey || anchorNode?.baseKey || '').trim() || null;
  const anchorKey = String(anchorNode?.key || '').trim();

  const mintIndexes = [];
  taskList.forEach((task, index) => {
    const handledBy = String(task?.handledBy || '').trim();
    const existingNodeKey = String(task?.nodeKey || '').trim();
    if (handledBy !== MISSION_TASK_HANDLED_BY.PEOPLE || existingNodeKey) return;
    mintIndexes.push(index);
  });

  if (!mintIndexes.length) {
    reportMintProgress(onProgress, {
      completed: 0,
      phase: 'done',
      total: 0,
    });
    return taskList;
  }

  if (!anchorKey || !user?.key) {
    throw new Error('Cannot mint workflow step tasks without anchor and user.');
  }

  const total = mintIndexes.length;

  reportMintProgress(onProgress, {
    completed: 0,
    phase: 'preparing',
    total,
  });

  const built = await Promise.all(mintIndexes.map(async (taskIndex) => {
    const task = taskList[taskIndex];
    const minted = await buildWorkflowStepTaskNode({
      anchorNode,
      assignees: task?.assignees,
      baseKey: resolvedBaseKey,
      description: task?.description,
      title: task?.title,
      user,
    });
    const recipe = readWorkflowTaskChecklistRecipe(task);
    return {
      minted,
      recipeItems: recipe.items,
      taskIndex,
    };
  }));

  reportMintProgress(onProgress, {
    completed: 0,
    phase: 'creating',
    total,
  });

  await createNodes(built.map(({ minted }) => ({
    key: minted.key,
    data: minted,
  })));

  reportMintProgress(onProgress, {
    completed: 0,
    phase: 'linking',
    total,
  });

  await upsertEdges(
    built.map(({ minted }) => ({
      nodeKeyA: anchorKey,
      nodeKeyB: String(minted.key || '').trim(),
      ...buildHierarchyEdgeOptions({
        parentNode: anchorNode,
        childNode: minted,
        baseKey: resolvedBaseKey || minted?.baseKey || null,
      }),
      skipActivity: true,
    })),
    {
      baseKey: resolvedBaseKey,
      freshChildren: true,
      skipActivity: true,
    },
  );

  const typeDef = getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null;
  const checklistTargets = built.filter(({ recipeItems }) => (
    Array.isArray(recipeItems) && recipeItems.length > 0
  ));

  if (checklistTargets.length > 0) {
    let checklistCompleted = 0;
    reportMintProgress(onProgress, {
      completed: 0,
      phase: 'checklists',
      total: checklistTargets.length,
    });

    await Promise.all(checklistTargets.map(async ({ minted, recipeItems }) => {
      try {
        await seedWorkflowStepTaskChecklist({
          recipeItems,
          stepNode: minted,
          typeDef,
          user,
        });
      } catch (error) {
        console.warn('[mintWorkflowStepTaskNodes] Task Checklist seed failed:', error);
      } finally {
        checklistCompleted += 1;
        reportMintProgress(onProgress, {
          completed: checklistCompleted,
          phase: 'checklists',
          total: checklistTargets.length,
        });
      }
    }));
  }

  const allResourceKeys = Array.from(new Set(
    built.flatMap(({ recipeItems }) => collectChecklistResourceNodeKeys(recipeItems)),
  ));

  if (allResourceKeys.length > 0) {
    reportMintProgress(onProgress, {
      completed: 0,
      phase: 'resources',
      total,
    });

    try {
      let resourceNodes = [];
      try {
        resourceNodes = await getNodesByKeys(allResourceKeys);
      } catch (error) {
        console.warn('[mintWorkflowStepTaskNodes] Failed to load checklist resources:', error);
        resourceNodes = [];
      }

      const nodesByKey = new Map(
        (Array.isArray(resourceNodes) ? resourceNodes : [])
          .filter((node) => node?.key)
          .map((node) => [String(node.key).trim(), node]),
      );

      const resourceLinks = built.flatMap(({ minted, recipeItems }) => (
        collectWorkflowStepChecklistResourceLinks({
          nodesByKey,
          recipeItems,
          stepNode: minted,
        })
      ));

      if (resourceLinks.length > 0) {
        await upsertEdges(resourceLinks, {
          baseKey: resolvedBaseKey,
          skipActivity: true,
        });
      }
    } catch (error) {
      console.warn('[mintWorkflowStepTaskNodes] Checklist resource link failed:', error);
    }
  }

  const nextTasks = taskList.map((task) => ({ ...task }));
  built.forEach(({ minted, taskIndex }) => {
    nextTasks[taskIndex] = {
      ...nextTasks[taskIndex],
      nodeKey: String(minted?.key || '').trim(),
    };
  });

  reportMintProgress(onProgress, {
    completed: total,
    phase: 'done',
    total,
  });

  return nextTasks;
}
